import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Divider,
    Chip,
    Tabs,
    Tab,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { styled } from '@mui/material/styles';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: '12px',
    },
}));

const SummaryCard = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #304FFF 0%, #1E40FF 100%)',
    borderRadius: '8px',
    padding: '16px',
    color: 'white',
    marginBottom: '16px',
}));

const InfoCard = styled(Box)(({ theme }) => ({
    backgroundColor: '#E8EEFF',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
    border: '1px solid rgba(255,255,255,0.3)',
}));

const ImportPreviewModal = ({ open, onClose, onConfirm, previewData, loading }) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const [tab, setTab] = useState(0);
    const [attachDocuments, setAttachDocuments] = useState(true);
    const [forceRefreshDocuments, setForceRefreshDocuments] = useState(false);

    const newTransactions = previewData?.transactions || [];
    const duplicateTransactions = previewData?.duplicateTransactions || [];
    const documentsAvailableToAttach =
        previewData?.documentsAvailableToAttach
        ?? duplicateTransactions.filter((r) => r.canAttachDocument).length;
    const documentsAvailableToRefresh =
        previewData?.documentsAvailableToRefresh
        ?? duplicateTransactions.filter((r) => r.canRefreshDocument || r.hasChequeImage).length;
    const hasDuplicateTab =
        !!previewData?.isFinsiteImport
        || duplicateTransactions.length > 0
        || (previewData?.duplicateCount || 0) > 0
        || (previewData?.skippedCount || 0) > 0;
    const newCount = previewData?.transactionsCount || newTransactions.length || 0;
    const isFinsite = !!previewData?.isFinsiteImport;

    useEffect(() => {
        if (open) {
            setTab(0);
            setAttachDocuments(true);
            setForceRefreshDocuments(false);
        }
    }, [open, previewData]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('he-IL', {
            style: 'currency',
            currency: 'ILS',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount ?? 0);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('he-IL');
    };

    const balanceChange = previewData ? previewData.balanceAfter - previewData.balanceBefore : 0;
    const isPositive = balanceChange >= 0;

    const skipReasonLabel = (reason) => {
        if (reason === 'EntryId') return t('FinsiteDuplicateAlreadyImported') || 'Already imported from Finsite';
        if (reason === 'ExistingTransaction') return t('FinsiteDuplicateExistingRow') || 'Matches existing bank row';
        if (reason === 'Suppressed') return t('FinsiteDuplicateSuppressed') || 'Blocked from re-import';
        return t('Duplicate') || 'Duplicate';
    };

    const activeRows = useMemo(() => {
        if (!hasDuplicateTab) return newTransactions;
        return tab === 0 ? newTransactions : duplicateTransactions;
    }, [hasDuplicateTab, tab, newTransactions, duplicateTransactions]);

    const renderTransactionsTable = (rows, { showSkipReason = false } = {}) => (
        <TableContainer component={Paper} sx={{ maxHeight: 350, borderRadius: '8px', boxShadow: '0 2px 8px rgba(48, 79, 255, 0.12)' }}>
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700, backgroundColor: '#E8EEFF', textAlign: 'right', direction: 'rtl' }}>
                            {t("Date")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, backgroundColor: '#E8EEFF', textAlign: 'right', direction: 'rtl' }}>
                            {t("Description")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, backgroundColor: '#E8EEFF', textAlign: 'right', direction: 'rtl' }}>
                            {t("Reference")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, backgroundColor: '#E8EEFF', textAlign: 'right', direction: 'rtl' }}>
                            {t("Amount")}
                        </TableCell>
                        {showSkipReason && (
                            <TableCell sx={{ fontWeight: 700, backgroundColor: '#E8EEFF', textAlign: 'right', direction: 'rtl' }}>
                                {t("Reason") || t("SkipReason") || "Reason"}
                            </TableCell>
                        )}
                        {showSkipReason && isFinsite && (
                            <TableCell sx={{ fontWeight: 700, backgroundColor: '#E8EEFF', textAlign: 'right', direction: 'rtl' }}>
                                {t("Document") || "Document"}
                            </TableCell>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows && rows.length > 0 ? (
                        rows.map((transaction, index) => (
                            <TableRow
                                key={`${transaction.finsiteEntryId || transaction.reference || index}-${index}`}
                                sx={{
                                    '&:hover': { backgroundColor: showSkipReason ? '#FDECE3' : '#E8EEFF' },
                                    backgroundColor: showSkipReason ? 'rgba(229, 124, 34, 0.06)' : 'inherit',
                                    '&:last-child td': { border: 0 }
                                }}
                            >
                                <TableCell sx={{ textAlign: 'right', direction: 'rtl', color: '#6c757d' }}>
                                    {formatDate(transaction.transactionDate)}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', direction: 'rtl' }}>
                                    {transaction.description || '-'}
                                </TableCell>
                                <TableCell sx={{ textAlign: 'right', direction: 'rtl', color: '#6c757d' }}>
                                    {transaction.reference || '-'}
                                </TableCell>
                                <TableCell
                                    sx={{
                                        textAlign: 'right',
                                        direction: 'rtl',
                                        color: transaction.amount >= 0 ? '#38A169' : '#E53E3E',
                                        fontWeight: 600
                                    }}
                                >
                                    {formatCurrency(transaction.amount)}
                                </TableCell>
                                {showSkipReason && (
                                    <TableCell sx={{ textAlign: 'right', direction: 'rtl', color: '#E57C22', fontSize: '0.85rem' }}>
                                        {skipReasonLabel(transaction.skipReason)}
                                    </TableCell>
                                )}
                                {showSkipReason && isFinsite && (
                                    <TableCell sx={{ textAlign: 'right', direction: 'rtl' }}>
                                        {transaction.canAttachDocument ? (
                                            <Chip
                                                size="small"
                                                label={t('FinsiteChequeCanAttach') || 'Check image available'}
                                                sx={{ backgroundColor: '#38A169', color: 'white', fontWeight: 600, height: 22 }}
                                            />
                                        ) : transaction.hasChequeImage ? (
                                            <Chip
                                                size="small"
                                                label={t('FinsiteChequeAlreadySaved') || 'Document already saved'}
                                                sx={{ backgroundColor: '#94A3B8', color: 'white', height: 22 }}
                                            />
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">—</Typography>
                                        )}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell
                                colSpan={showSkipReason ? (isFinsite ? 6 : 5) : 4}
                                align="center"
                                sx={{ py: 4 }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    {showSkipReason
                                        ? (t("NoDuplicatesFound") || t("NoTransactions"))
                                        : t("NoTransactions")}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const canConfirm = !!previewData
        && !loading
        && (
            newCount > 0
            || (isFinsite && attachDocuments && documentsAvailableToAttach > 0)
            || (isFinsite && forceRefreshDocuments && documentsAvailableToRefresh > 0)
        );

    const confirmLabel = isFinsite
        && newCount === 0
        && (forceRefreshDocuments
            ? documentsAvailableToRefresh > 0
            : documentsAvailableToAttach > 0 && attachDocuments)
        ? (forceRefreshDocuments
            ? (t('FinsiteFixDocuments') || 'Fix & re-save documents')
            : (t('FinsiteSaveDocuments') || 'Save documents'))
        : t('ConfirmImport');

    const handleConfirmClick = () => {
        if (!canConfirm) return;
        onConfirm?.({
            attachDocumentsForExisting: isFinsite
                ? (attachDocuments || forceRefreshDocuments)
                : true,
            forceRefreshDocuments: isFinsite ? forceRefreshDocuments : false,
        });
    };

    return (
        <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth dir={direction}>
            <DialogTitle style={{ textAlign: 'right', paddingBottom: '8px' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Chip
                            label={`${t('PreviewNewTransactions') || 'New'}: ${newCount}`}
                            size="small"
                            sx={{
                                fontWeight: 'bold',
                                backgroundColor: '#304FFF',
                                color: 'white'
                            }}
                        />
                        {hasDuplicateTab && (
                            <Chip
                                label={`${t('Duplicates') || 'Duplicates'}: ${previewData?.duplicateCount || duplicateTransactions.length || 0}`}
                                size="small"
                                sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#E57C22',
                                    color: 'white'
                                }}
                            />
                        )}
                        {isFinsite && documentsAvailableToAttach > 0 && (
                            <Chip
                                label={`${t('FinsiteDocumentsAvailable') || 'Documents'}: ${documentsAvailableToAttach}`}
                                size="small"
                                sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#38A169',
                                    color: 'white'
                                }}
                            />
                        )}
                        {isFinsite && documentsAvailableToRefresh > 0 && (
                            <Chip
                                label={`${t('FinsiteDocumentsRefreshable') || 'Can fix'}: ${documentsAvailableToRefresh}`}
                                size="small"
                                sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#0D9488',
                                    color: 'white'
                                }}
                            />
                        )}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t("Preview")} - {previewData?.isFinsiteImport ? t("FetchFromFinsite") : t("ImportFile")}
                    </Typography>
                </Box>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 3 }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : previewData ? (
                    <>
                        {/* Summary Section */}
                        <SummaryCard>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 1.5 }}>
                                <Box sx={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '6px', padding: '12px', border: 'none' }}>
                                    <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                                        {t("BalanceBefore")}
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25, fontSize: '1rem' }}>
                                        {formatCurrency(previewData.balanceBefore)}
                                    </Typography>
                                </Box>
                                <Box sx={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '6px', padding: '12px', border: 'none' }}>
                                    <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                                        {t("BalanceAfter")}
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.25, fontSize: '1rem' }}>
                                        {formatCurrency(previewData.balanceAfter)}
                                    </Typography>
                                </Box>
                                <Box sx={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '6px', padding: '12px', border: 'none' }}>
                                    <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.7rem' }}>
                                        {t("BalanceChange")}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={0.5} mt={0.25}>
                                        {isPositive ? (
                                            <TrendingUpIcon sx={{ fontSize: '20px' }} />
                                        ) : (
                                            <TrendingDownIcon sx={{ fontSize: '20px' }} />
                                        )}
                                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                                            {formatCurrency(balanceChange)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                            {/* Bank Balance, System Balance, and Balance After Comparison */}
                            {(previewData.bankBalance !== null && previewData.bankBalance !== undefined) && (
                                <InfoCard>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
                                        <Box>
                                            <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.75rem', display: 'block', mb: 0.5,color: '#304FFF' }}>
                                                {t("BankBalance")}
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: '#304FFF' }}>
                                                {formatCurrency(previewData.bankBalance)}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.75rem', display: 'block', mb: 0.5,color: '#304FFF' }}>
                                                {t("SystemBalance")}
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: '#000000' }}>
                                                {formatCurrency(previewData.systemBalance)}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.75rem', display: 'block', mb: 0.5,color: '#304FFF' }}>
                                                {t("BalanceAfter")}
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: previewData.balanceAfter !== null && previewData.bankBalance !== null && Math.abs(previewData.balanceAfter - previewData.bankBalance) > 0.01 ? '#E53E3E' : '#000000' }}>
                                                {formatCurrency(previewData.balanceAfter)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {previewData.balanceAfter !== null && previewData.bankBalance !== null && Math.abs(previewData.balanceAfter - previewData.bankBalance) > 0.01 && (
                                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                                            <Typography variant="caption" sx={{ color: '#E53E3E', fontSize: '0.75rem' }}>
                                                {t("BalanceMismatch")}: {formatCurrency(Math.abs(previewData.balanceAfter - previewData.bankBalance))}
                                            </Typography>
                                        </Box>
                                    )}
                                </InfoCard>
                            )}
                        </SummaryCard>

                        {hasDuplicateTab ? (
                            <>
                                <Tabs
                                    value={tab}
                                    onChange={(_, v) => setTab(v)}
                                    sx={{
                                        mb: 1.5,
                                        minHeight: 40,
                                        '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 },
                                        '& .Mui-selected': { color: '#304FFF' },
                                        '& .MuiTabs-indicator': { backgroundColor: '#304FFF' },
                                    }}
                                >
                                    <Tab
                                        label={`${t('PreviewNewTransactions') || 'New'} (${newTransactions.length})`}
                                    />
                                    <Tab
                                        label={`${t('Duplicates') || 'Duplicates'} (${duplicateTransactions.length})`}
                                        sx={{ '&.Mui-selected': { color: '#E57C22' } }}
                                    />
                                </Tabs>
                                {tab === 1 && (
                                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                        {t('FinsiteDuplicatesTabHelp') ||
                                            'These rows already exist (Excel/manual or previous Finsite fetch) and will be skipped on confirm.'}
                                    </Typography>
                                )}
                                {renderTransactionsTable(activeRows, { showSkipReason: tab === 1 })}
                            </>
                        ) : (
                            <>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, textAlign: 'left' }}>
                                    {t("Transactions")}
                                </Typography>
                                {renderTransactionsTable(newTransactions)}
                            </>
                        )}

                        {(isFinsite && (documentsAvailableToAttach > 0 || documentsAvailableToRefresh > 0)) && (
                            <Box
                                sx={{
                                    mt: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: direction === 'rtl' ? 'flex-end' : 'flex-start',
                                    gap: 0.5,
                                }}
                            >
                                {documentsAvailableToAttach > 0 && (
                                    <FormControlLabel
                                        sx={{ mr: 0, ml: 0 }}
                                        control={
                                            <Checkbox
                                                checked={attachDocuments}
                                                onChange={(e) => setAttachDocuments(e.target.checked)}
                                                disabled={forceRefreshDocuments}
                                                sx={{ color: '#304FFF', '&.Mui-checked': { color: '#304FFF' } }}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                {t('FinsiteAttachDocumentsOption') ||
                                                    `Save check images for existing transactions (${documentsAvailableToAttach})`}
                                            </Typography>
                                        }
                                    />
                                )}
                                {documentsAvailableToRefresh > 0 && (
                                    <FormControlLabel
                                        sx={{ mr: 0, ml: 0 }}
                                        control={
                                            <Checkbox
                                                checked={forceRefreshDocuments}
                                                onChange={(e) => {
                                                    const on = e.target.checked;
                                                    setForceRefreshDocuments(on);
                                                    if (on) setAttachDocuments(true);
                                                }}
                                                sx={{ color: '#0D9488', '&.Mui-checked': { color: '#0D9488' } }}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" sx={{ textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                                                {t('FinsiteForceRefreshDocumentsOption') ||
                                                    `Re-fetch check images and save under the correct path (${documentsAvailableToRefresh})`}
                                            </Typography>
                                        }
                                    />
                                )}
                            </Box>
                        )}
                    </>
                ) : (
                    <Box textAlign="center" py={4}>
                        <Typography color="text.secondary">{t("NoPreviewData")}</Typography>
                    </Box>
                )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{ minWidth: '100px' }}
                >
                    {t("Cancel")}
                </Button>
                <Button
                    onClick={handleConfirmClick}
                    variant="contained"
                    disabled={!canConfirm}
                    sx={{
                        minWidth: '100px',
                        fontWeight: 600,
                        backgroundColor: '#304FFF',
                        '&:hover': {
                            backgroundColor: '#1E40FF',
                        }
                    }}
                >
                    {confirmLabel}
                </Button>
            </DialogActions>
        </StyledDialog>
    );
};

export default ImportPreviewModal;
